import { useEffect, useRef, MutableRefObject, Dispatch } from 'react';
import { io, Socket } from 'socket.io-client';
import {
  SimulationAction,
  SessionStatus,
  StateUpdatePayload,
  MutationEventPayload,
  SessionEndPayload,
  SessionPausedPayload,
  TurnRejectedPayload,
  TimeWarningPayload,
  StateSnapshotPayload,
  AuthErrorPayload,
  WS_EVENTS,
} from '../types/simulation.types';
import {
  HEARTBEAT_INTERVAL_MS,
  RECONNECTION_ATTEMPTS,
  RECONNECTION_DELAY,
  RECONNECTION_DELAY_MAX,
  SOCKET_NAMESPACE,
} from '../constants/simulation.constants';

const SOCKET_URL =
  process.env.REACT_APP_WS_URL ?? process.env.REACT_APP_API_BASE_URL ?? '';

interface UseSimulationSocketParams {
  simSessionId: string | null;
  interviewSessionId: string | null;
  sessionStatus: SessionStatus;
  socketRef: MutableRefObject<Socket | null>;
  dispatch: Dispatch<SimulationAction>;
}

export function useSimulationSocket({
  simSessionId,
  interviewSessionId,
  sessionStatus,
  socketRef,
  dispatch,
}: UseSimulationSocketParams) {
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!simSessionId || !interviewSessionId) return;
    if (sessionStatus !== 'active' && sessionStatus !== 'paused') return;

    const token = localStorage.getItem('access-token');
    const sessionToken = localStorage.getItem('sessionToken');

    const socket = io(SOCKET_URL + SOCKET_NAMESPACE, {
      transports: ['websocket'],
      auth: {
        token,
        sessionToken,
        simSessionId,
        interviewSessionId,
      },
      reconnection: true,
      reconnectionAttempts: RECONNECTION_ATTEMPTS,
      reconnectionDelay: RECONNECTION_DELAY,
      reconnectionDelayMax: RECONNECTION_DELAY_MAX,
    });

    socketRef.current = socket;

    // ─── Connection Events ─────────────────────────
    socket.on('connect', () => {
      dispatch({ type: 'SET_CONNECTED', payload: true });
      dispatch({ type: 'SET_RECONNECTING', payload: false });
      startHeartbeat(socket);
    });

    socket.on('disconnect', (reason) => {
      dispatch({ type: 'SET_CONNECTED', payload: false });
      stopHeartbeat();
      if (reason !== 'io client disconnect') {
        dispatch({ type: 'SET_RECONNECTING', payload: true });
      }
    });

    socket.io.on('reconnect_attempt', () => {
      dispatch({ type: 'SET_RECONNECTING', payload: true });
    });

    socket.io.on('reconnect_failed', () => {
      dispatch({
        type: 'SET_ERROR',
        payload: 'Connection lost. Please refresh the page.',
      });
      dispatch({ type: 'SET_RECONNECTING', payload: false });
    });

    socket.io.on('reconnect', () => {
      // On successful reconnect, request full state rebuild
      socket.emit(WS_EVENTS.RECONNECT, { simSessionId });
    });

    // ─── Server → Client Events ────────────────────
    socket.on(
      WS_EVENTS.STATE_UPDATE,
      (payload: StateUpdatePayload) => {
        dispatch({ type: 'STATE_UPDATE', payload });
      },
    );

    socket.on(
      WS_EVENTS.MUTATION_EVENT,
      (payload: MutationEventPayload) => {
        dispatch({ type: 'MUTATION_EVENT', payload });
      },
    );

    socket.on(WS_EVENTS.SESSION_END, (payload: SessionEndPayload) => {
      dispatch({ type: 'SESSION_END', payload });
      stopHeartbeat();
    });

    socket.on(
      WS_EVENTS.TURN_REJECTED,
      (payload: TurnRejectedPayload) => {
        dispatch({ type: 'TURN_REJECTED', payload });
      },
    );

    socket.on(
      WS_EVENTS.TIME_WARNING,
      (payload: TimeWarningPayload) => {
        dispatch({ type: 'TIME_WARNING', payload });
      },
    );

    socket.on(
      WS_EVENTS.STATE_SNAPSHOT,
      (payload: StateSnapshotPayload) => {
        dispatch({ type: 'STATE_SNAPSHOT', payload });
      },
    );

    socket.on(WS_EVENTS.SESSION_PAUSED, (payload: SessionPausedPayload) => {
      dispatch({ type: 'SESSION_PAUSED', payload });
      stopHeartbeat();
    });

    socket.on(WS_EVENTS.AUTH_ERROR, (payload: AuthErrorPayload) => {
      dispatch({ type: 'AUTH_ERROR', payload });
      stopHeartbeat();
      socket.disconnect();
    });

    return () => {
      stopHeartbeat();
      socket.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simSessionId, interviewSessionId, sessionStatus]);

  function startHeartbeat(socket: Socket) {
    stopHeartbeat();
    heartbeatRef.current = setInterval(() => {
      socket.emit(WS_EVENTS.HEARTBEAT, { timestamp: Date.now() });
    }, HEARTBEAT_INTERVAL_MS);
  }

  function stopHeartbeat() {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }
}
